<template>
  <div class="board-filters">
    <slot />

    <form
      v-if="showSearch"
      class="board-filters__search-form"
      @submit.prevent="updateQuery({ search: searchInputText.trim(), page: defaultPage })"
    >
      <NebulaInput
        class="board-filters__search-input"
        :placeholder="$t('Search')"
        size="s"
        :text="searchInputText"
        type="text"
        @input="searchInputText = $event"
      />
      <NebulaButton
        class="board-filters__search-button"
        :aria-label="$t('Search')"
        icon-left="search"
        is-submit
        size="s"
        type="flat"
      />
    </form>

    <div class="board-filters__dropdowns-wrapper">
      <HomeFilterDropdown
        v-if="showFilterDropdown"
        class="board-filters__dropdown"
        :label="$t('Filter')"
        :options="filterDropdownOptions"
        :selected-id="query.filter"
        @selectOption="updateQuery({
          filter: $event,
          page: defaultPage,
        })"
      />
      <HomeFilterDropdown
        v-if="showSortOptions"
        class="board-filters__dropdown"
        :label="$t('Sort')"
        :options="sortOptions"
        :selected-id="selectedSortId"
        @selectOption="updateSort"
      />

      <NebulaButtonGroup
        v-if="showLayoutOptions"
        :aria-label="$t('Layout')"
        role="group"
      >
        <template #buttons>
          <input
            id="board-filters__layouts--grid"
            type="radio"
            class="comet-button__input"
            name="board-filters__toggle"
            :checked="query.layout !== 'list'"
            @change="updateQuery({ layout: 'grid' })"
          >
          <label
            for="board-filters__layouts--grid"
            class="board-filters__layouts board-filters__layouts--grid"
            role="button"
          >
            <NebulaButton
              class="nebula-button-group__item"
              size="s"
              icon-left="grid"
              is-static
            />
            <span class="comet-screenreader-only">
              {{ $t('Grid Layout') }}
            </span>
          </label>
          <input
            id="board-filters__layouts--list"
            type="radio"
            class="comet-button__input"
            name="board-filters__toggle"
            :checked="query.layout === 'list'"
            @change="updateQuery({ layout: 'list' })"
          >
          <label
            for="board-filters__layouts--list"
            class="board-filters__layouts board-filters__layouts--list"
            role="button"
          >
            <NebulaButton
              class="nebula-button-group__item"
              size="s"
              icon-left="list"
              is-static
            />
            <span class="comet-screenreader-only">
              {{ $t('List Layout') }}
            </span>
          </label>
        </template>
      </NebulaButtonGroup>
    </div>
  </div>
</template>

<script>
import { NebulaButton, NebulaButtonGroup, NebulaInput } from '@discoveryedu/nebula-components';
import { get, first } from 'lodash-es';
import { mapState } from 'pinia';
import HomeFilterDropdown from '@/components/home/HomeFilterDropdown.vue';
import homeMixin from '@/mixins/home';
import {
  useUserStore,
} from '@/stores';

export default {
  components: {
    HomeFilterDropdown,
    NebulaButton,
    NebulaButtonGroup,
    NebulaInput,
  },
  mixins: [homeMixin],
  name: 'BoardFilters',
  props: {
    sidebarFilter: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      assignedFilterOptions: [
        {
          id: 'assigned_from_me',
          label: this.$t('All Assignments'),
        },
        {
          id: 'completed',
          label: this.$t('Completed'),
        },
        {
          id: 'past_due',
          label: this.$t('Past Due'),
        },
      ],
      filterOptions: [
        {
          id: 'all',
          label: this.$t('All'),
        },
        {
          id: 'drafts',
          label: this.$t('Drafts'),
        },
        {
          id: 'shared',
          label: this.$t('Shared'),
        },
        {
          id: 'boards',
          label: this.$t('Boards'),
        },
        {
          id: 'slideshows',
          label: this.$t('Slideshows'),
        },
        {
          id: 'lessons',
          label: this.$t('Lessons'),
          editorialOnly: true,
        },
      ],
      searchInputText: '',
      trashSortOptions: [
        {
          id: 'deleted-date-desc',
          label: this.$t('Deleted Date Descending'),
          sort: 'deleted_on',
          sortOrder: 'desc',
        },
        {
          id: 'deleted-date-asc',
          label: this.$t('Deleted Date Ascending'),
          sort: 'deleted_on',
          sortOrder: 'asc',
        },
      ],
      defaultSortOptions: [
        {
          id: 'updated-date-desc',
          label: this.$t('Updated Date Descending'),
          sort: 'updated_on',
          sortOrder: 'desc',
        },
        {
          id: 'updated-date-asc',
          label: this.$t('Updated Date Ascending'),
          sort: 'updated_on',
          sortOrder: 'asc',
        },
        {
          id: 'created-date-desc',
          label: this.$t('Created Date Descending'),
          sort: 'created_on',
          sortOrder: 'desc',
        },
        {
          id: 'created-date-asc',
          label: this.$t('Created Date Ascending'),
          sort: 'created_on',
          sortOrder: 'asc',
        },
        {
          id: 'alphabetical-asc',
          label: this.$t('Title A–Z'),
          sort: 'alphabetical',
          sortOrder: 'asc',
        },
        {
          id: 'alphabetical-desc',
          label: this.$t('Title Z–A'),
          sort: 'alphabetical',
          sortOrder: 'desc',
        },
      ],
    };
  },
  mounted() {
    this.setSearchInputText();
  },
  watch: {
    $route() {
      this.setSearchInputText();
    },
  },
  computed: {
    ...mapState(useUserStore, [
      'isDeUser',
      'userIsStudent',
    ]),
    filterDropdownOptions() {
      const filterOptions = this.sidebarFilter === 'ASSIGNED'
        ? this.assignedFilterOptions
        : this.filterOptions;

      // Only editorial users have the "Lesson" filter option
      return filterOptions.filter((filterOption) => (this.isDeUser || !filterOption.editorialOnly));
    },
    selectedSortId() {
      const selectedOption = this.sortOptions.find((opt) => (
        opt.sort === this.query.sort && opt.sortOrder === this.query.sortOrder
      ));

      return selectedOption
        ? selectedOption.id
        : get(first(this.sortOptions), 'id');
    },
    showFilterDropdown() {
      return !['ASSIGNED', 'ACTIVITY_TEMPLATES', 'TRASH'].includes(this.sidebarFilter);
    },
    showSortOptions() {
      return this.sidebarFilter !== 'ACTIVITY_TEMPLATES';
    },
    showSearch() {
      return !['ACTIVITY_TEMPLATES', 'TRASH'].includes(this.sidebarFilter);
    },
    showLayoutOptions() {
      return !['TRASH'].includes(this.sidebarFilter);
    },
    sortOptions() {
      return this.sidebarFilter === 'TRASH' ? this.trashSortOptions : this.defaultSortOptions;
    },
  },
  methods: {
    setSearchInputText() {
      this.searchInputText = this.query.search;
    },
    updateQuery(queryUpdate) {
      const currentQuery = { ...this.query };
      const updateKeys = Object.keys(queryUpdate);
      const firstKey = first(updateKeys);
      /*
        If the first updated query value is not equal to the value
        in the current query string, then apply all updates.
      */
      if (currentQuery[firstKey] !== queryUpdate[firstKey]) {
        updateKeys.forEach((key) => {
          currentQuery[key] = queryUpdate[key];
        });
      }
      this.pushQuery(currentQuery);
    },
    updateSort(id) {
      const newParams = this.sortOptions.find((opt) => opt.id === id);

      if (newParams) {
        this.pushQuery({
          ...this.query,
          page: this.defaultPage,
          sort: newParams.sort,
          sortOrder: newParams.sortOrder,
        });
      }
    },
  },
};
</script>

<style lang="stylus">
.board-filters {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin: 0 0 $nebula-space-3x;
  position: relative;
  z-index: 10;

  &__dropdowns-wrapper {
    align-items: flex-end;
    display: flex;

    > div {
      margin-block: 0 $nebula-space-1x;
      margin-inline: $nebula-space-1x 0;
    }
  }

  &__search-form {
    margin-bottom: $nebula-space-1x;
    position: relative;
  }

  &__layouts .nebula-button {
    .comet-button__input:checked + & {
      background-color: var(--nebula-button-group-active-item-background-color);
      color: var(--nebula-button-group-active-item-text-color);
    }
  }

  &__search-button {
    border-end-end-radius: 7px; // fit inside the 1px border, which has 8px radius
    border-end-start-radius: 0;
    border-start-end-radius: 7px;
    border-start-start-radius: 0;
    bottom: 1px;
    inset-inline-end: 1px;
    margin: 0;
    position: absolute;
    top: 1px;
  }

  &__search-input {
    margin: 0;
    padding-inline-end: $nebula-space-4x; // clear the search button
  }

  html[dir="rtl"] & {
    .board-filters__search-button {
      transform: scaleX(-1);
    }
  }
}
</style>
